import { Button, Intent, Position, Toast, Toaster } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Button as DesignSystemButton } from 'vet-bones/design-system/components';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { ErrorNotificationMessage } from 'src/components/ErrorNotificationMessage';
import { getNotificationTestId } from 'src/constants/testIds';
import { Notification } from 'src/store/state/notifications';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const Notifications: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const notifications = useReduxSelector(
    (state) => state.notifications.notifications
  );

  const handleDismiss = (id: string) =>
    dispatch(notificationsActionCreators.dismissedNotification(id));

  const handleDismissAll = () =>
    dispatch(notificationsActionCreators.dismissedAllNotifications());

  return (
    <Toaster
      className="sd-notifications-toaster"
      position={Position.BOTTOM_RIGHT}
    >
      {notifications.map(
        ({
          id,
          className,
          type,
          data,
          message,
          ...notification
        }: Notification) => {
          const testId = getNotificationTestId(type);
          const isDanger = notification.intent === Intent.DANGER;

          let messageElement: JSX.Element;
          if (isDanger) {
            messageElement = (
              <ErrorNotificationMessage
                message={message as string}
                testId={testId}
              />
            );
          } else if (data?.actions) {
            messageElement = (
              <>
                <div className="sd-toast-actionable-message">
                  <span data-testid={testId}>{message}</span>
                </div>
                <div className="sd-toast-actionable-actions">
                  {data.actions.map((action) => (
                    <Button
                      key={`${id}-${action.text}`}
                      data-testid={action.actionButtonTestId}
                      minimal
                      onClick={() => {
                        if (action.onClickAction) {
                          dispatch(action.onClickAction);
                        }
                        if (data.dismissOnAction) {
                          handleDismiss(id);
                        }
                      }}
                      text={action.text}
                    />
                  ))}
                </div>
              </>
            );
          } else {
            messageElement = <span data-testid={testId}>{message}</span>;
          }

          return (
            <Toast
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...notification}
              key={id}
              className={classNames(className, {
                'sd-toast-actionable': data?.actions || isDanger,
                'sd-toast-danger': isDanger,
              })}
              message={messageElement}
              onDismiss={() => handleDismiss(id)}
            />
          );
        }
      )}
      {notifications.length > 1 ? (
        <div className="sd-notifications-close-all">
          <DesignSystemButton
            onClick={handleDismissAll}
            size="sm"
            testId={getNotificationTestId('close-all')}
            variant="secondary-solid"
          >
            {copy.notifications.defaults.closeAll}
          </DesignSystemButton>
        </div>
      ) : null}
    </Toaster>
  );
};
